import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Instagram, CheckCircle2, X, Heart } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { listPostCare, updateTattooFollowup } from "@/lib/api";
import { formatDate, waLink, igDMLink, composeFollowupMessage } from "@/lib/format";
import { useMessageTemplates } from "@/lib/useTemplates";

export default function PostCare() {
  const [items, setItems] = useState([]);
  const templates = useMessageTemplates();

  const load = () => listPostCare(7).then(setItems).catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const markSent = async (id) => {
    await updateTattooFollowup(id, "enviado");
    toast.success("Marcado como enviado");
    load();
  };

  const dismiss = async (id) => {
    await updateTattooFollowup(id, "dispensado");
    toast.success("Dispensado");
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Cuidado"
        title="Pós-Atendimento"
        description="Clientes que fizeram tatuagem há 7 dias ou mais e ainda não receberam o follow-up de cicatrização."
      />

      {items.length === 0 ? (
        <Empty title="Tudo em dia" hint="Nenhuma mensagem de pós-atendimento pendente." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="postcare-list">
          {items.map((t) => {
            const firstName = (t.client_name || "").split(" ")[0];
            const msg = composeFollowupMessage(firstName, templates.followup);
            return (
              <div key={t.id} className="elegant-card p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#F4E8D6] flex items-center justify-center shrink-0">
                    <Heart className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/clientes/${t.client_id}`} className="font-serif-display text-xl text-[#2E332C] hover:text-[#7B8B6F] block truncate">
                      {t.client_name}
                    </Link>
                    <p className="text-xs text-[#5C6658] mt-1">{t.description}</p>
                    <p className="text-xs text-[#5C6658] mt-0.5">Tatuagem em {formatDate(t.date)}</p>
                  </div>
                </div>

                <div className="bg-[#F5F2E8] rounded-xl p-3 text-xs text-[#5C6658] italic mb-4 whitespace-pre-line">
                  {msg}
                </div>

                <div className="flex flex-wrap gap-2">
                  {t.client_phone && (
                    <Button asChild className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white flex-1" data-testid={`postcare-wa-${t.id}`}>
                      <a href={waLink(t.client_phone, msg)} target="_blank" rel="noreferrer">
                        <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                      </a>
                    </Button>
                  )}
                  {t.client_instagram && (
                    <Button asChild variant="outline" className="rounded-full border-[#E8E4D9]" data-testid={`postcare-ig-${t.id}`}>
                      <a href={igDMLink(t.client_instagram)} target="_blank" rel="noreferrer">
                        <Instagram className="w-4 h-4 mr-2" /> DM
                      </a>
                    </Button>
                  )}
                  <Button onClick={() => markSent(t.id)} variant="outline" className="rounded-full border-[#E8E4D9] text-[#7B8B6F]" data-testid={`postcare-done-${t.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Enviei
                  </Button>
                  <Button onClick={() => dismiss(t.id)} variant="ghost" className="rounded-full text-[#5C6658]" data-testid={`postcare-skip-${t.id}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
